import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { deleteObject, getStorage, ref, uploadBytes } from 'firebase/storage';
import { app, db, storage } from '../config/firebase';
import type {
  BwaFileMeta,
  IntakeTarifTier,
  NeukundenIntakeDoc,
  NeukundenIntakeFormData,
} from '../types/intake';
import {
  buildVorabMandantNr,
  extractCoreFromMandantNr,
  normalizeVorabMandantNr,
  resolveCoachInitialLetter,
} from '../utils/mandantNrFormat';

const INTAKE_COLLECTION = 'neukunden_intake';

const DEFAULT_MANDANTEN_PARAMETER = {
  wochensatz: 4.33,
  auslastungsfaktor: 75,
  auslastungsfaktorMax: 100,
  zusatzumsatz: 30,
  gewinnlinie: 26,
  investlinie: 5,
  verlust1: 15,
  liquiditaet: 10,
};

export function parseIntakeNettopreis(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.').replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
}

export function packageDefaultNettopreis(packageType: string): number | null {
  if (packageType === 'insider') return 167;
  if (packageType === 'insider_solo') return 94;
  if (packageType === 'basic') return 49;
  return null;
}

function splitInhaberName(full: string): { vorname: string; nachname: string } {
  const parts = String(full || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { vorname: '', nachname: '' };
  if (parts.length === 1) return { vorname: parts[0], nachname: '' };
  return { vorname: parts[0], nachname: parts.slice(1).join(' ') };
}

function tarifFlags(tarif: IntakeTarifTier) {
  if (tarif === 'solo') {
    return {
      isSolo: true,
      forceSoloTarif: true,
      hasEmployeeEval: false,
      isTeamProPlus: false,
      packageTier: 'solo' as const,
      tarifStatus: 'Solo' as const,
      lizenzTyp: 'insider',
    };
  }
  if (tarif === 'teamBase' || tarif === 'team_base') {
    return {
      isSolo: false,
      forceSoloTarif: false,
      hasEmployeeEval: false,
      isTeamProPlus: false,
      packageTier: 'teamBase' as const,
      tarifStatus: 'TeamBase' as const,
      lizenzTyp: 'basic',
    };
  }
  if (tarif === 'team_plus' || tarif === 'teamProPlus') {
    return {
      isSolo: false,
      forceSoloTarif: false,
      hasEmployeeEval: true,
      isTeamProPlus: true,
      packageTier: 'teamProPlus' as const,
      tarifStatus: 'TeamPro+' as const,
      lizenzTyp: 'insider',
    };
  }
  return {
    isSolo: false,
    forceSoloTarif: false,
    hasEmployeeEval: true,
    isTeamProPlus: false,
    packageTier: 'teamPro' as const,
    tarifStatus: 'TeamPro' as const,
    lizenzTyp: 'insider',
  };
}

/** Default-Tarif-Hinweis aus Paketwahl (Admin kann überschreiben). */
export function suggestTarifFromPackage(packageType: string): IntakeTarifTier {
  if (packageType === 'insider_solo') return 'solo';
  return 'team';
}

function sanitizeSalon(name: string): string {
  return String(name || 'salon')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48) || 'salon';
}

/** Dateiname-Basis aus Mandantenname (ohne Umlaute/Sonderzeichen). */
export function personFileBase(name: string): string {
  const base = String(name || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return base || 'Mandant';
}

/** Anzeigename / Download-Name: BWA_<Mandantin>.pdf bzw. _2, _3 … */
export function buildBwaFileName(mandantName: string, index = 0, total = 1): string {
  const base = personFileBase(mandantName);
  if (total <= 1) return `BWA_${base}.pdf`;
  return `BWA_${base}_${index + 1}.pdf`;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => {
      reject(new Error(`${label} dauert zu lange (>${Math.round(ms / 1000)}s). Bitte erneut versuchen.`));
    }, ms);
    promise.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (err) => {
        window.clearTimeout(t);
        reject(err);
      }
    );
  });
}

function mapFirebaseErr(err: any, fallback: string): Error {
  const code = String(err?.code || '');
  const msg = String(err?.message || '');
  if (/dauert zu lange/i.test(msg)) {
    return new Error(`${msg} Bitte Internet prüfen und erneut senden.`);
  }
  if (code.includes('unauthorized') || code.includes('permission-denied') || /permission|unauthorized/i.test(msg)) {
    return new Error('Upload nicht erlaubt (Firebase-Rechte). Bitte Seite neu laden und erneut senden.');
  }
  if (code.includes('canceled') || /abort|cancel/i.test(msg)) {
    return new Error('Upload abgebrochen. Bitte erneut senden.');
  }
  if (code.includes('retry-limit') || /network|offline|Failed to fetch/i.test(msg)) {
    return new Error('Netzwerkproblem beim Upload. Verbindung prüfen und erneut senden.');
  }
  return new Error(msg || fallback);
}

export type UploadProgressFn = (info: { index: number; total: number; fileName: string }) => void;

function resolveIntakeStorage() {
  const configured = String(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '').trim();
  const projectId = String(process.env.REACT_APP_FIREBASE_PROJECT_ID || '').trim();
  // Live-Bucket ist *.firebasestorage.app — *.appspot.com existiert hier nicht (404).
  const bucket =
    configured ||
    (projectId ? `${projectId}.firebasestorage.app` : '');
  if (bucket) {
    try {
      return getStorage(app, `gs://${bucket}`);
    } catch {
      /* fallback unten */
    }
  }
  return storage;
}

export async function uploadBwaFiles(
  salonName: string,
  files: File[],
  onProgress?: UploadProgressFn,
  /** Mandantin/Inhaber — Dateiname wird darauf umbenannt */
  mandantName?: string
): Promise<BwaFileMeta[]> {
  const person = String(mandantName || salonName || '').trim() || 'Mandant';
  const folder = `neukunden_intake/${Date.now()}_${sanitizeSalon(salonName)}`;
  const out: BwaFileMeta[] = [];
  const store = resolveIntakeStorage();

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const displayName = buildBwaFileName(person, i, files.length);
    onProgress?.({ index: i + 1, total: files.length, fileName: displayName });
    const storagePath = `${folder}/${displayName}`;
    const storageRef = ref(store, storagePath);
    try {
      await withTimeout(
        uploadBytes(storageRef, file, { contentType: 'application/pdf' }),
        45_000,
        `Upload „${displayName}“`
      );
    } catch (err: any) {
      throw mapFirebaseErr(err, `Upload von „${displayName}“ fehlgeschlagen.`);
    }
    out.push({
      name: displayName,
      storagePath,
      downloadUrl: '',
      size: file.size,
      uploadedAt: new Date().toISOString(),
      deleted: false,
    });
  }
  return out;
}

export async function submitNeukundenIntake(
  data: NeukundenIntakeFormData,
  bwaFiles: BwaFileMeta[]
): Promise<string> {
  const createdAt = new Date().toISOString();
  const nettopreis =
    parseIntakeNettopreis(data.nettopreis) ??
    packageDefaultNettopreis(String(data.packageType || ''));
  const payload: Omit<NeukundenIntakeDoc, 'id'> = {
    salonName: data.salonName.trim(),
    rechtsform: data.rechtsform || 'einzel',
    firmenbezeichnung: data.rechtsform === 'einzel' ? '' : data.firmenbezeichnung.trim(),
    inhaber: data.inhaber.trim(),
    kontoinhaber: (data.kontoinhaberIdentisch ? data.inhaber : data.kontoinhaber).trim(),
    kontoinhaberIdentisch: Boolean(data.kontoinhaberIdentisch),
    strasse: data.strasse.trim(),
    plz: data.plz.trim(),
    ort: data.ort.trim(),
    iban: data.iban.replace(/\s+/g, '').toUpperCase(),
    ustId: data.ustId.trim(),
    email: data.email.trim(),
    whatsapp: data.whatsapp.trim(),
    website: data.website.trim(),
    social: data.social.trim(),
    packageType: data.packageType as NeukundenIntakeDoc['packageType'],
    nettopreis,
    vertragAccepted: true,
    sepaAccepted: true,
    bwaLater: Boolean(data.bwaLater),
    status: 'neu',
    mandantennummer: null,
    bearbeiter: null,
    createdAt,
    bwaDeletedAt: null,
    bwaFiles,
    vorabkundeId: null,
  };

  const refDoc = await withTimeout(
    addDoc(collection(db, INTAKE_COLLECTION), {
      ...payload,
      createdAtTs: serverTimestamp(),
    }),
    45_000,
    'Anmeldung speichern'
  ).catch((err) => {
    throw mapFirebaseErr(err, 'Anmeldung konnte nicht gespeichert werden.');
  });

  const hubUrl = `${window.location.origin}/coach/intake-hub`;
  const paketLabel =
    payload.packageType === 'insider'
      ? 'INSIDER'
      : payload.packageType === 'insider_solo'
        ? 'INSIDER Solo'
        : 'BASIC';
  const nettoLabel =
    payload.nettopreis !== null && payload.nettopreis !== undefined
      ? `${String(payload.nettopreis).replace('.', ',')} € netto / Monat`
      : '—';

  // Mail darf die Anmeldung nicht blockieren
  void addDoc(collection(db, 'mail_queue'), {
    type: 'change_request',
    email: 'erfolg@friseur-coaching.de',
    subject: `🚨 Neue Mandanten-Anmeldung: ${payload.salonName} - ${payload.inhaber}`,
    messageHtml: [
      `<p><strong>Neue Mandanten-Anmeldung</strong></p>`,
      `<p>Salon: ${payload.salonName}<br/>Rechtsform: ${payload.rechtsform === 'gmbh' ? 'GmbH' : payload.rechtsform === 'gbr' ? 'GbR' : 'Einzelunternehmen'}${payload.firmenbezeichnung ? `<br/>Firmenbezeichnung: ${payload.firmenbezeichnung}` : ''}<br/>Inhaber: ${payload.inhaber}<br/>Ort: ${payload.plz} ${payload.ort}<br/>Tel: ${payload.whatsapp}<br/>Mail: ${payload.email}</p>`,
      `<p>Paket: ${paketLabel}<br/>Nettopreis: ${nettoLabel}<br/>BWA-Dateien: ${bwaFiles.length}${payload.bwaLater ? ` (noch keine Datei, Nachreichen über ${window.location.origin}/bwa-upload)` : ''}</p>`,
      `<p><a href="${hubUrl}">Zum Coach-Hub</a></p>`,
    ].join(''),
    mailTitle: 'Neukunden-Intake',
    participantName: payload.inhaber,
    appBaseUrl: window.location.origin,
    status: 'pending',
    createdAt: serverTimestamp(),
  }).catch((err) => {
    console.warn('[intake] mail_queue:', err);
  });

  return refDoc.id;
}

export async function deleteBwaFilesFromStorage(docId: string, files: BwaFileMeta[]): Promise<void> {
  for (const file of files) {
    if (!file.storagePath || file.deleted) continue;
    try {
      await deleteObject(ref(storage, file.storagePath));
    } catch (err) {
      console.warn('[intake] BWA delete:', file.storagePath, err);
    }
  }
  await updateDoc(doc(db, INTAKE_COLLECTION, docId), {
    bwaFiles: [],
    bwaDeletedAt: new Date().toISOString(),
  });
}

export async function deleteNeukundenIntake(docId: string, files: BwaFileMeta[]): Promise<void> {
  for (const file of files) {
    if (!file.storagePath) continue;
    try {
      await deleteObject(ref(storage, file.storagePath));
    } catch (err) {
      console.warn('[intake] BWA delete:', file.storagePath, err);
    }
  }
  await deleteDoc(doc(db, INTAKE_COLLECTION, docId));
}

export async function updateNeukundenIntake(
  docId: string,
  patch: Partial<
    Pick<
      NeukundenIntakeDoc,
      | 'status'
      | 'bearbeiter'
      | 'mandantennummer'
      | 'vorabkundeId'
      | 'nettopreis'
      | 'salonName'
      | 'rechtsform'
      | 'firmenbezeichnung'
      | 'inhaber'
      | 'kontoinhaber'
      | 'kontoinhaberIdentisch'
      | 'strasse'
      | 'plz'
      | 'ort'
      | 'iban'
      | 'ustId'
      | 'email'
      | 'whatsapp'
      | 'website'
      | 'social'
      | 'packageType'
      | 'lastContentEditedAt'
      | 'lastContentEditedBy'
    >
  >
): Promise<void> {
  await updateDoc(doc(db, INTAKE_COLLECTION, docId), patch);
}

/** Mail an Admin, wenn Coach einen Intake als erledigt markiert. */
export async function notifyIntakeErledigt(
  row: Pick<
    NeukundenIntakeDoc,
    'id' | 'salonName' | 'inhaber' | 'email' | 'bearbeiter' | 'mandantennummer' | 'nettopreis' | 'packageType'
  >
): Promise<void> {
  const salon = String(row.salonName || '—').trim();
  const inhaber = String(row.inhaber || '—').trim();
  const bearbeiter = String(row.bearbeiter || '—').trim();
  const nr = String(row.mandantennummer || '—').trim();
  const fee =
    typeof row.nettopreis === 'number' && Number.isFinite(row.nettopreis)
      ? `${String(row.nettopreis).replace('.', ',')} €`
      : '—';
  const hubUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/coach/intake-hub`;
  await addDoc(collection(db, 'mail_queue'), {
    type: 'change_request',
    email: 'erfolg@friseur-coaching.de',
    subject: `Neukunde erledigt: ${salon} (${inhaber})`,
    messageHtml: [
      `<p><strong>Neukunden-Intake als erledigt markiert</strong></p>`,
      `<p>Salon: ${salon}<br/>Inhaber: ${inhaber}<br/>E-Mail: ${row.email || '—'}<br/>Bearbeiter: ${bearbeiter}<br/>Mandant-Nummer: ${nr}<br/>Nettopreis: ${fee}<br/>Paket: ${row.packageType || '—'}</p>`,
      `<p>Hinweis: Übernahme in die Coach-Liste erfolgt <strong>nur manuell durch den Admin</strong>.</p>`,
      `<p><a href="${hubUrl}">Zum Neukunden-Hub</a></p>`,
    ].join(''),
    mailTitle: 'Neukunde erledigt',
    participantName: inhaber,
    appBaseUrl: typeof window !== 'undefined' ? window.location.origin : '',
    status: 'pending',
    createdAt: serverTimestamp(),
  });
}

export type CreateVorabFromIntakeInput = {
  intake: NeukundenIntakeDoc;
  /** Roh-Nr. vom Coach (z. B. 31/2023 oder Altformat) */
  mandantennummer: string;
  tarif: IntakeTarifTier;
  /** YYYY-MM, default aktueller Monat */
  zugangAb?: string;
  /** Coach-Name oder ID für Initial */
  betreuerNameOrId?: string | null;
  betreuerId?: string | null;
  team?: any[];
  monthlyFeeOverride?: number | null;
};

/**
 * Legt aus einem Neukunden-Intake einen Vorabkunden in kalkulation_mandanten an.
 * Nummer → sauberes `MA NN/YYYY-X`. Tarif nur als Flags/Badge (nicht in der Nr.).
 */
export async function createVorabkundeFromIntake(
  input: CreateVorabFromIntakeInput
): Promise<{ mandantId: string; mandantNr: string }> {
  const { intake } = input;
  if (!intake.id) throw new Error('Intake ohne ID.');

  if (intake.status !== 'abgeschlossen') {
    throw new Error('Status muss „Erledigt“ sein — Übernahme blockiert.');
  }

  const rawNr = String(input.mandantennummer || intake.mandantennummer || '').trim();
  if (!rawNr) throw new Error('Mandant-Nummer fehlt — Übernahme blockiert.');

  const feeCheck =
    input.monthlyFeeOverride ??
    (typeof intake.nettopreis === 'number' ? intake.nettopreis : null);
  if (feeCheck == null || !(Number(feeCheck) > 0)) {
    throw new Error('Nettopreis fehlt — Übernahme blockiert.');
  }

  const coachKey = input.betreuerNameOrId || intake.bearbeiter || '';
  const coachInitial = resolveCoachInitialLetter(coachKey, input.team);
  const cleanVorabNr =
    normalizeVorabMandantNr(rawNr, coachInitial) ||
    buildVorabMandantNr(extractCoreFromMandantNr(rawNr) || rawNr, coachInitial);

  if (!cleanVorabNr) {
    throw new Error('Mandant-Nummer ungültig — erwartet z. B. 22/2022.');
  }

  let zugangAb = String(input.zugangAb || '').trim();
  if (!zugangAb || !/^\d{4}-\d{2}$/.test(zugangAb)) {
    const now = new Date();
    zugangAb = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  const [y, m] = zugangAb.split('-');
  const vertragsbeginn = `${m}/${y}`;

  const fee =
    input.monthlyFeeOverride ??
    (typeof intake.nettopreis === 'number' ? intake.nettopreis : null) ??
    packageDefaultNettopreis(intake.packageType) ??
    0;

  const flags = tarifFlags(input.tarif);
  const { vorname, nachname } = splitInhaberName(intake.inhaber);
  const salonname = (intake.firmenbezeichnung || intake.salonName || '').trim() || 'Salon';

  // Land aus USt-IdNr. oder IBAN ableiten (AT / CH / IT / DE)
  const ustClean = String(intake.ustId || '').replace(/\s+/g, '').toUpperCase();
  const ibanClean = String(intake.iban || '').replace(/\s+/g, '').toUpperCase();
  const detectedLand: 'DE' | 'AT' | 'CH' | 'IT' =
    ustClean.startsWith('ATU') || ibanClean.startsWith('AT')
      ? 'AT'
      : ustClean.startsWith('CHE') || ibanClean.startsWith('CH')
        ? 'CH'
        : ustClean.startsWith('IT') || ibanClean.startsWith('IT')
          ? 'IT'
          : 'DE';

  // Coach-Initial nur für Nummern-Format — KEIN Betreuer am Mandanten
  // (sonst erscheint die Akte sofort beim Coach). Zuweisung nur manuell im Admin.
  const clientPayload = {
    firmenname: salonname,
    vorname,
    nachname,
    email: intake.email || '',
    mailadresse: intake.email || '',
    mandantNr: cleanVorabNr,
    customerNumber: cleanVorabNr,
    existingCustomerNumber: cleanVorabNr,
    betreuer: '',
    coachId: '',
    coach: '',
    monthlyFee: fee,
    Land: detectedLand,
    land: detectedLand,
    ustId: intake.ustId || '',
    status: 'aktiv',
    isArchived: false,
    isActive: true,
    isLegacy: true,
    isExistingCustomer: true,
    isBestandskunde: true,
    isSolo: flags.isSolo,
    forceSoloTarif: flags.forceSoloTarif,
    hasEmployeeEval: flags.hasEmployeeEval,
    isTeamProPlus: flags.isTeamProPlus,
    insiderTeamleaderPlusChecked: flags.isTeamProPlus,
    insiderTeamleaderStandardChecked: !flags.isSolo && !flags.isTeamProPlus && flags.hasEmployeeEval,
    insiderSoloChecked: flags.isSolo,
    packageTier: flags.packageTier,
    tarifStatus: flags.tarifStatus,
    createdAt: new Date().toISOString(),
    vertragsbeginn,
    strasse: intake.strasse || '',
    plz: intake.plz || '',
    ort: intake.ort || '',
    clientData: {
      salonname,
      vorname,
      nachname,
      mailadresse: intake.email || '',
      email: intake.email || '',
      mandantNr: cleanVorabNr,
      customerNumber: cleanVorabNr,
      existingCustomerNumber: cleanVorabNr,
      betreuer: '',
      coachId: '',
      coach: '',
      monthlyFee: fee,
      vertragsbeginn,
      isSolo: flags.isSolo,
      forceSoloTarif: flags.forceSoloTarif,
      hasEmployeeEval: flags.hasEmployeeEval,
      isTeamProPlus: flags.isTeamProPlus,
      insiderTeamleaderPlusChecked: flags.isTeamProPlus,
      insiderTeamleaderStandardChecked: !flags.isSolo && !flags.isTeamProPlus && flags.hasEmployeeEval,
      insiderSoloChecked: flags.isSolo,
      packageTier: flags.packageTier,
      tarifStatus: flags.tarifStatus,
      strasse: intake.strasse || '',
      plz: intake.plz || '',
      ort: intake.ort || '',
      Land: detectedLand,
      land: detectedLand,
      ustId: intake.ustId || '',
      unternehmensform:
        intake.rechtsform === 'gmbh' ? 'GmbH' : intake.rechtsform === 'gbr' ? 'GbR' : 'Einzelunternehmen',
      lizenzDetails: { typ: flags.lizenzTyp },
      contact: { firstName: vorname, lastName: nachname },
      ...DEFAULT_MANDANTEN_PARAMETER,
    },
  };

  const refDoc = await addDoc(collection(db, 'kalkulation_mandanten'), {
    ...clientPayload,
    lastAdminUpdate: serverTimestamp(),
  });

  await updateDoc(doc(db, INTAKE_COLLECTION, intake.id), {
    mandantennummer: cleanVorabNr,
    status: 'abgeschlossen',
    vorabkundeId: refDoc.id,
  });

  return { mandantId: refDoc.id, mandantNr: cleanVorabNr };
}

const BWA_UPLOAD_COLLECTION = 'bwa_uploads';

export async function submitBwaOnlyUpload(input: {
  name: string;
  email: string;
  kundennummer: string;
  file: File;
}): Promise<void> {
  const base = personFileBase(input.name);
  const fileName = buildBwaFileName(input.name);
  const storagePath = `neukunden_intake/bwa_upload/${Date.now()}_${base}/${fileName}`;
  await uploadBytes(ref(storage, storagePath), input.file, { contentType: 'application/pdf' });
  const name = input.name.trim();
  const email = input.email.trim();
  const kundennummer = input.kundennummer.trim();
  await addDoc(collection(db, BWA_UPLOAD_COLLECTION), {
    name,
    email,
    kundennummer,
    fileName,
    storagePath,
    createdAt: new Date().toISOString(),
    createdAtTs: serverTimestamp(),
  });

  const hubUrl = `${window.location.origin}/coach/intake-hub`;
  await addDoc(collection(db, 'mail_queue'), {
    type: 'change_request',
    email: 'erfolg@friseur-coaching.de',
    subject: `BWA eingegangen: ${name}`,
    messageHtml: [
      `<p><strong>Neue BWA</strong></p>`,
      `<p>Name: ${name}<br/>E-Mail: ${email || '—'}<br/>Mandant-Nummer: ${kundennummer || '—'}<br/>Datei: ${fileName}</p>`,
      `<p><a href="${hubUrl}">Zum Coach-Hub</a></p>`,
    ].join(''),
    mailTitle: 'BWA-Upload',
    participantName: name,
    appBaseUrl: window.location.origin,
    status: 'pending',
    createdAt: serverTimestamp(),
  });
}

export async function deleteBwaUpload(docId: string, storagePath: string): Promise<void> {
  if (storagePath) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (err) {
      console.warn('[bwa-upload] delete:', storagePath, err);
    }
  }
  await deleteDoc(doc(db, BWA_UPLOAD_COLLECTION, docId));
}
